import { wrapIntrospect } from '@adornis/introspect/server/introspect.js';
import type { ChatCompletionMessageParam } from '@adornis/llm/server/token-js/index.js';
import { MailExtractionResult } from '../../db/MailExtractionResult.js';
import { checkIsRequest } from './1.1-check-is-request.js';
import { buildCanonicalDocument } from './1.5-build-canonical-document.js';
import { preprocessProductText } from './2.0-preprocess-product-text.js';
import { extractCompanyInformation } from './2.1-extract-company-information.js';
import { extractProductInformation } from './2.2-extract-product-information.js';
import { extractRequestInformation } from './2.3-extract-request-information.js';

// Human-readable step names — used both as the introspection node operation (so the
// processing details read as plain language) and as the labels the "Nachvollziehen"
// timeline shows. Order matches the pipeline.
export const EXTRACTION_STEP_LABELS = [
  'Prüfen ob Anfrage',
  'Klartext-Dokument erstellen',
  'Produktdaten auslesen',
  'Firma erkennen',
  'Bestelldaten erfassen',
  'Produkte zuordnen',
] as const;

export async function runExtractionByContext({
  mailID,
  context,
  senderEmail,
  unreadableAttachments,
}: {
  mailID: string;
  context: ChatCompletionMessageParam[];
  senderEmail?: string | null;
  /** Attachments the context could not carry, so the result can own up to the gap. */
  unreadableAttachments?: string[];
}): Promise<MailExtractionResult> {
  // 1.1
  const isRequest = await wrapIntrospect('Prüfen ob Anfrage', checkIsRequest)(context);

  // 1.5 - Build the canonical document: one faithful Markdown transcript of the whole
  // inquiry. This is now the SINGLE input for every downstream step — they parse this
  // clean, unified document instead of re-reading the raw multimodal context. If it
  // fails, downstream cannot run, so we let the error propagate (runExtractionByMail
  // logs it); a partial result without products would be misleading.
  const result = isRequest
    ? await runExtractionFromCanonicalDocument({
        mailID,
        canonicalDocument: await wrapIntrospect('Klartext-Dokument erstellen', buildCanonicalDocument)(context),
        senderEmail,
      })
    : new MailExtractionResult({ mailID, notAnOrderDocument: true });

  // However the run went, it has to own up to what went into it: an offer missing the
  // positions of an unreadable attachment must not look like one the extraction got wrong.
  result.unreadableAttachments = unreadableAttachments?.length ? unreadableAttachments : null;
  return result;
}

// Steps 2.0-2.3 only, starting from an already-built canonical document (Klartext). Split out
// of runExtractionByContext so the production-regression corpus (verified-correct mails that
// already have their canonicalDocument persisted) can replay just the structured-extraction
// steps without re-running OCR/canonicalization (1.0/1.1/1.5) for every case — that would be
// slow, costly at corpus scale, and would reintroduce OCR non-determinism into what is meant
// to be a check of the deterministic-input extraction steps.
export async function runExtractionFromCanonicalDocument({
  mailID,
  canonicalDocument,
  senderEmail,
}: {
  mailID: string;
  canonicalDocument: string;
  senderEmail?: string | null;
}): Promise<MailExtractionResult> {
  const mailExtractionResult = new MailExtractionResult({ mailID });
  mailExtractionResult.notAnOrderDocument = false;
  mailExtractionResult.canonicalDocument = canonicalDocument;

  // 2.0 - Parse the document into raw product signals + deterministic classification
  const preprocessedText = await wrapIntrospect('Produktdaten auslesen', preprocessProductText)(canonicalDocument);
  console.log({ preprocessedText });

  // 2.1
  mailExtractionResult.requestingCompany = await wrapIntrospect('Firma erkennen', extractCompanyInformation)(
    canonicalDocument,
    senderEmail,
  );

  // 2.3 - Extract request-level information (best effort). This is order-level
  // metadata (request ID, delivery date, contact) — an LLM hiccup here must not
  // abort the whole extraction and discard the products/companies already set.
  // Runs before 2.2 because its request number is the primary source for each
  // product's requestID (SAP PURCH_NO_C); without it every position would fall
  // back to a position-level reference or bare position number.
  try {
    const requestInformation = await wrapIntrospect(
      'Bestelldaten erfassen',
      extractRequestInformation,
    )(canonicalDocument);
    mailExtractionResult.requestID = requestInformation.requestID;
    mailExtractionResult.desiredDeliveryDate = requestInformation.desiredDeliveryDate;
    mailExtractionResult.reference = requestInformation.reference;
  } catch (err) {
    console.error('Request-level information extraction failed — continuing without it.', err);
  }

  // 2.2 - Map & match products from the parsed signals
  const productInformation = await wrapIntrospect('Produkte zuordnen', extractProductInformation)(
    preprocessedText,
    mailExtractionResult.requestID,
  );
  mailExtractionResult.extractedProducts = productInformation;

  return mailExtractionResult;
}
